import React, { ReactNode, useEffect, useState } from "react";
import { addDays } from "date-fns";
import { getPackageConfig } from "components/ContentfulRenderer/ContentfulPackages/getPackageConfig";
import ErrorMessage from "components/ErrorMessage/ErrorMessage";
import { formatErrorMessage } from "utils/validation";
import ReviewsSection from "components/ContentfulRenderer/ContentfulComponent/ReviewsSection/ReviewsSection";
import Benefits from "components/ContentfulRenderer/ContentfulComponent/Benefits/Benefits";
import InsurancePackagesTable from "components/ContentfulRenderer/ContentfulPackages/InsurancePackagesTable/InsurancePackagesTable";
import MobileFullPackages from "components/ContentfulRenderer/ContentfulPackages/MobileFullPackages/MobileFullPackages";
import ShowOnMedia from "ui-kit/ShowOnMedia";
import InnerLayout from "components/InnerLayout/InnerLayout";
import { getCopays } from "utils/site";
import { getAmountToPay } from "utils/insurance/getAmountToPay";
import { formatCurrency } from "utils/utils";
import { StepProps } from "../../types";
import { useWebTranslation } from "translations";
import { useCurrentInsuranceByAnimalId } from "components/Signup/store/selectors/insurance";
import {
  useAllValidAnimals,
  useCurrentAnimal,
} from "components/Signup/store/selectors/animals";
import { ProductEnum } from "../../../../../../__generated__/globalTypes";
import { Container } from "ui-kit/Layout";
import Gutter from "ui-kit/Gutter";
import { Button } from "ui-kit/Button";
import { Typography } from "ui-kit/Typography";
import InsurancePackages from "components/Signup/components/InsurancePackages";
import { useSignupDispatch } from "components/Signup/store/hooks";
import {
  updateBillingInterval,
  updateInsuranceById,
} from "components/Signup/store/slices/animals";
import DateInput from "components/DateInput/DateInput";
import {
  validStartDate,
  amountToPay as amountToPayWithoutDiscount,
  getEarliestAllowedStartDate,
} from "utils/insurance";
import StickyBottom from "components/Signup/components/StickyBottom/StickyBottom";
import ChoosePaymentInterval from "components/Signup/components/ChoosePaymentInterval";
import DiscountCode from "components/Signup/components/DiscountCode";
import ChooseDeductible from "components/Signup/components/ChooseDeductible";
import Overlay from "components/Signup/components/Overlay";
import { BuyButton } from "components/Signup/components/BuyButton";
import SignupDocuments from "components/Signup/components/SignupDocuments/SignupDocuments";
import { UpsertDraft_upsertDraft_policyPreviews } from "../../../../../../__generated__/UpsertDraft";
import {
  useError,
  useIsLoading,
} from "components/Signup/store/selectors/metadata";
import { AnimalsTabs } from "components/Signup/components/AnimalsTabs";
import { QuotePreviewBottomBar } from "components/Signup/components/QuotePreviewBottomBar";
import { TuvBadges } from "components/TuvBadges";
import { QPBanner } from "components/Signup/components/tractive/QPBanner";

const findQuotePreview = ({
  drafts,
  limit,
  deductible,
  copay,
}: {
  drafts?: UpsertDraft_upsertDraft_policyPreviews["plans"] | null;
  limit: number;
  deductible: number;
  copay: number;
}) =>
  drafts?.find(
    (draft) =>
      draft?.limit === limit &&
      draft.deductible === deductible &&
      draft.copay === copay
  );

const QuotePreview = ({ next, transitioning, isValid }: StepProps) => {
  const [overlayDisplay, setOverlayDisplay] = useState<{
    title: string;
    content: ReactNode;
    full?: boolean;
  } | null>(null);
  const { t } = useWebTranslation();
  const dispatch = useSignupDispatch();
  const animals = useAllValidAnimals();

  const animal = useCurrentAnimal();
  const {
    copay,
    startDate,
    discountCode,
    deductible,
    limit,
    billingInterval,
    drafts,
    id,
  } = useCurrentInsuranceByAnimalId(animal.id);
  const error = useError();
  const isLoading = useIsLoading();

  const [opacity, setOpacity] = useState(0);
  useEffect(() => {
    setTimeout(() => {
      setOpacity(1);
    });
  }, []);

  const currentQuote = findQuotePreview({
    drafts,
    limit,
    deductible,
    copay,
  });

  const packageConfig = getPackageConfig({
    product: animal.species as unknown as ProductEnum,
  });

  if (!packageConfig) {
    return null;
  }

  packageConfig.benefits = packageConfig.benefits.filter(
    (benefit) => benefit.name !== "deductible"
  );

  const packagePrices = packageConfig.packages.map((pack) =>
    getAmountToPay({
      ...findQuotePreview({
        drafts,
        limit: pack.limit,
        deductible,
        copay,
      }),
      billingInterval,
    })
  );

  const selectedIndex = packageConfig.packages.findIndex(
    (pack) => limit === pack.limit
  );

  const compareOverlay = {
    title: t("quote_preview.compare_packages_overlay"),
    content: (
      <>
        <ShowOnMedia desktop={false} tablet={true} mobile={true}>
          <MobileFullPackages
            config={packageConfig}
            defaultSelectedIndex={selectedIndex}
            expanded
          />
        </ShowOnMedia>
        <ShowOnMedia desktop={true} tablet={false} mobile={false}>
          <InsurancePackagesTable config={packageConfig} />
        </ShowOnMedia>
      </>
    ),
    full: true,
  };

  const amountToPay = getAmountToPay({
    ...findQuotePreview({
      drafts,
      limit,
      deductible,
      copay,
    }),
    billingInterval,
  });

  return (
    <div style={{ opacity, transition: "250ms ease-in" }}>
      {animals.length > 1 && <AnimalsTabs />}
      <InsurancePackages
        packageConfig={packageConfig}
        packagePrices={packagePrices}
        selectedIndex={selectedIndex}
        onChange={({ limit }) =>
          dispatch(updateInsuranceById({ id, update: { limit } }))
        }
        onInfo={(info) => setOverlayDisplay(info)}
        onCompare={() => setOverlayDisplay(compareOverlay)}
      />

      <Container>
        <Gutter size={4} />
        <Button
          variant="custom"
          textColor="blue300"
          icon="chevron-right"
          title={t("quote_preview.compare_packages")}
          onPress={() => setOverlayDisplay(compareOverlay)}
        />

        <Gutter size={4} />
        <Typography variant="h3" color="grey500">
          {t("choose_start_date.title")}
        </Typography>
        <Gutter size={3} />
        <Typography>{t("quote_preview.start_date")}</Typography>
        <Gutter size={3} />
        <DateInput
          value={startDate ?? getEarliestAllowedStartDate()}
          onChange={(startDate: { date: string }) => {
            dispatch(
              updateInsuranceById({ id, update: { startDate: startDate.date } })
            );
          }}
          min={
            new Date(
              validStartDate({
                birthday: animal.birthday!,
                startDate,
              })
            )
          }
          max={addDays(new Date(), 364)}
          style={{ fontFamily: "var(--font-eina-semi-bold)" }}
        />

        <Gutter size={6} />
        <ChooseDeductible
          currentTotal={amountToPayWithoutDiscount({
            ...currentQuote,
            billingInterval,
          })}
          currentDeductible={deductible}
          currentCopay={copay}
          deductibles={[]}
          copays={getCopays().map((copay) => ({
            value: copay,
            total: amountToPayWithoutDiscount({
              ...findQuotePreview({
                drafts,
                limit,
                deductible,
                copay,
              }),
              billingInterval,
            }),
          }))}
          onChangeDeductibleCopay={(_, copay) => {
            dispatch(updateInsuranceById({ id, update: { copay } }));
          }}
          onInfo={({ title, content }) =>
            setOverlayDisplay({
              title,
              content,
            })
          }
        />

        {animals.length === 1 ? (
          <>
            <Gutter size={6} />
            <ChoosePaymentInterval
              value={billingInterval}
              onChange={(billingInterval) =>
                dispatch(updateBillingInterval(billingInterval))
              }
            />
          </>
        ) : null}

        <Gutter size={6} />
        <QPBanner species={animal.species} />
        <Gutter size={6} />
        <DiscountCode
          amountToPay={amountToPay}
          discount={{
            appliedCode: discountCode ?? "",
            onSubmit: (discountCode) => {
              dispatch(updateInsuranceById({ id, update: { discountCode } }));
            },
            inProgress: isLoading,
          }}
          fees={currentQuote?.fees}
          billingInterval={billingInterval}
        />
        {error && (
          <>
            <Gutter size={10} />
            <ErrorMessage>
              <Typography color="error">{formatErrorMessage(error)}</Typography>
            </ErrorMessage>
          </>
        )}
      </Container>

      <InnerLayout backgroundColor="white" hideHeader>
        <Gutter size={6} />
        <TuvBadges style={{ margin: "auto" }} />
        <Gutter size={4} />
        <Typography variant="h3" style={{ textAlign: "center" }}>
          {t("home.review_title")}
        </Typography>
        <Gutter size={4} />
        <ReviewsSection />
        <Gutter size={10} />
        <Typography variant="h3" style={{ textAlign: "center" }}>
          {t("benefits.title")}
        </Typography>
        <Gutter size={4} />
        <Benefits />
        <Gutter size={8} />
      </InnerLayout>

      {animals.length > 1 ? (
        <QuotePreviewBottomBar next={next} />
      ) : (
        <>
          <StickyBottom
            button={
              <BuyButton
                oldAmount={
                  amountToPay.old ? formatCurrency(amountToPay.old) : null
                }
                newAmount={formatCurrency(amountToPay.new)}
                onPress={next}
                loading={isLoading || transitioning}
                disabled={!isValid}
              />
            }
            transparent
          />
          <Gutter size={4} />
        </>
      )}

      <Gutter size={4} />
      <SignupDocuments
        documents={[
          {
            title: t("quote_preview.germany.ipid.health"),
            url: "https://docs.lassie.co/ger/de/ipid/latest/Produktubersicht-Krankenversicherung.pdf",
          },
          {
            title: t("quote_preview.germany.ipid.op"),
            url: "https://docs.lassie.co/ger/de/ipid/latest/Produktubersicht-OP-Versicherung.pdf",
          },
          {
            title: t("quote_preview.germany.tac.health_and_op"),
            url: "https://docs.lassie.co/ger/de/terms-and-conditions/latest/Allgemeine-Versicherungsbedingungen-OP-und-Krankenversicherung.pdf",
          },
        ]}
      />
      <Overlay
        display={overlayDisplay}
        onRemove={() => setOverlayDisplay(null)}
      />
    </div>
  );
};

export default QuotePreview;
